import React, { useEffect, useRef, useState } from 'react';
import './PreviewTempoSelector.scss';
import { RadioGroup } from '@mui/material';
import ExpandableRadioButton from '../../../../components/ExpandableRadioButton/ExpandableRadioButton';
import { TimelineTempo } from '../../../../slices/marketplace/timeline/models/enums/timeline-tempo.enum';
import InfoTooltip from '../../../../components/info-tooltip/info-tooltip';
import createNotification from '../../../../utils/createNotification';

interface PreviewTempoSelectorProps {
    currentTempo: TimelineTempo;
    onTempoChange: (newTempo: TimelineTempo) => void;
    setIsPreviewTempo: (isPreview: boolean) => void;
}

const PreviewTempoSelector = (props: PreviewTempoSelectorProps) => {
    const [previewTempo, setPreviewTempo] = useState<TimelineTempo>(
        props.currentTempo
    );
    const revertTimeoutRef = useRef<number | null>(null);

    useEffect(() => {
        setPreviewTempo(props.currentTempo);
    }, [props.currentTempo]);

    useEffect(() => {
        if (previewTempo === props.currentTempo) {
            if (revertTimeoutRef.current !== null) {
                clearTimeout(revertTimeoutRef.current);
                revertTimeoutRef.current = null;
            }
            props.setIsPreviewTempo(false);
            return;
        }

        props.setIsPreviewTempo(true);

        if (revertTimeoutRef.current !== null) {
            clearTimeout(revertTimeoutRef.current);
        }

        revertTimeoutRef.current = window.setTimeout(() => {
            setPreviewTempo(props.currentTempo);
            props.onTempoChange(props.currentTempo);
            props.setIsPreviewTempo(false);
            revertTimeoutRef.current = null;
        }, 60000);

        return () => {
            if (revertTimeoutRef.current !== null) {
                clearTimeout(revertTimeoutRef.current);
            }
        };
    }, [previewTempo, props.currentTempo]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newTempo = Number(event.target.value) as TimelineTempo;
        try {
            props.onTempoChange(newTempo);
            setPreviewTempo(newTempo);
        } catch (error) {
            createNotification((error as Error).message, 'error');
        }
    };

    return (
        <div className="preview-tempo-selector">
            <div className="preview-tempo-selector__label">
                <span>Preview Tempos</span>
                <InfoTooltip
                    idText="preview-tempo-info-tooltip"
                    text="This will preview for 60s then revert back to your currently selected pace."
                />
            </div>
            <RadioGroup value={previewTempo} onChange={handleChange} row>
                <ExpandableRadioButton
                    disabled={false}
                    value={TimelineTempo.SLOW}
                    label={`Slow${
                        props.currentTempo === TimelineTempo.SLOW
                            ? ' (current)'
                            : ''
                    }`}
                    isExpanded={false}
                    expandableContent={<></>}
                />
                <ExpandableRadioButton
                    disabled={false}
                    value={TimelineTempo.MEDIUM}
                    label={`Medium${
                        props.currentTempo === TimelineTempo.MEDIUM
                            ? ' (current)'
                            : ''
                    }`}
                    isExpanded={false}
                    expandableContent={<></>}
                />
                <ExpandableRadioButton
                    disabled={false}
                    value={TimelineTempo.FAST}
                    label={`Fast${
                        props.currentTempo === TimelineTempo.FAST
                            ? ' (current)'
                            : ''
                    }`}
                    isExpanded={false}
                    expandableContent={<></>}
                />
            </RadioGroup>
        </div>
    );
};

export default PreviewTempoSelector;
